/* LÉMEIN — true-alpha probe (2026-09-18)
   House contract (lemein-v2.css blocks 19 + 20): a product image multiplies
   onto its tile so a white-background render drops its white; a TRUE-ALPHA
   cut-out must never multiply, or the garment and the model's skin darken
   by 8-16% per channel.

   Blocks 19/20 enforce that on filename suffixes (-o, -r, -tr ...). Suffixes
   go stale: the old "-a.png" rule matched nothing for days after a media
   swap. This script makes the contract depend on the pixels instead. Each
   product PNG/WebP is fetched once at 48px wide, its alpha channel is read,
   and a real cut-out gets the ".lm-alpha" class that blocks 19/20 already
   exempt. The suffix list stays as the first-paint fallback; verdicts are
   cached per file in localStorage, so a returning visitor pays nothing.
   Same-origin (/cdn/shop/), so the canvas is never tainted. */
(() => {
  const KEY = 'lm-alpha-v1';
  const FILE = /\/cdn\/shop\/(?:files|products)\/[^?#]+\.(?:png|webp)(?:[?#]|$)/i;
  let cache = {};
  try { cache = JSON.parse(localStorage.getItem(KEY) || '{}') || {}; } catch (e) { cache = {}; }
  const save = () => { try { localStorage.setItem(KEY, JSON.stringify(cache)); } catch (e) {} };

  const fileOf = (img) => {
    const src = img.getAttribute('src') || '';
    if (!FILE.test(src)) return null;
    try { return new URL(src, location.href).pathname; } catch (e) { return null; }
  };

  const measure = (path) => fetch(path + '?width=48', { credentials: 'omit' })
    .then((r) => (r.ok ? r.blob() : Promise.reject(r.status)))
    .then((b) => createImageBitmap(b))
    .then((bmp) => {
      const c = document.createElement('canvas');
      c.width = bmp.width; c.height = bmp.height;
      const ctx = c.getContext('2d', { willReadFrequently: true });
      ctx.drawImage(bmp, 0, 0);
      const d = ctx.getImageData(0, 0, c.width, c.height).data;
      for (let i = 3; i < d.length; i += 4) if (d[i] < 250) return true;
      return false;
    });

  /* One job per file, at most 4 in flight. Images near the viewport jump the
     queue; everything else (hidden gallery slides, late cards) waits behind
     them, so it is classified before it is shown. */
  const jobs = new Map(); /* path -> { promise, run } */
  const queue = [];
  let active = 0;
  const pump = () => {
    while (active < 4 && queue.length) {
      const path = queue.shift();
      active++;
      jobs.get(path).run().finally(() => { active--; pump(); });
    }
  };

  const probe = (path, urgent) => {
    if (path in cache) return Promise.resolve(cache[path]);
    let job = jobs.get(path);
    if (!job) {
      job = {};
      job.promise = new Promise((resolve) => {
        job.run = () => measure(path)
          .then((alpha) => { cache[path] = alpha; save(); resolve(alpha); })
          .catch(() => resolve(null)) /* unknown: leave the suffix rule in charge */
          .finally(() => jobs.delete(path));
      });
      jobs.set(path, job);
      urgent ? queue.unshift(path) : queue.push(path);
    } else if (urgent) {
      const i = queue.indexOf(path);
      if (i > 0) { queue.splice(i, 1); queue.unshift(path); }
    }
    pump();
    return job.promise;
  };

  const apply = (img, urgent) => {
    const path = fileOf(img);
    if (!path) return;
    img.dataset.lmAlphaFor = path;
    if (path in cache) { img.classList.toggle('lm-alpha', cache[path]); return; }
    probe(path, urgent).then((alpha) => {
      if (img.dataset.lmAlphaFor !== path || alpha === null) return; /* src changed meanwhile */
      img.classList.toggle('lm-alpha', alpha);
    });
  };

  const io = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        for (const e of entries) if (e.isIntersecting) { io.unobserve(e.target); apply(e.target, true); }
      }, { rootMargin: '600px' })
    : null;

  const watch = (img) => {
    const path = fileOf(img);
    if (!path) return;
    if (!(path in cache) && io) io.observe(img);
    apply(img, false);
  };

  const scan = (root) => {
    if (root.tagName === 'IMG') watch(root);
    else if (root.querySelectorAll) root.querySelectorAll('img').forEach(watch);
  };

  scan(document);
  new MutationObserver((muts) => {
    for (const m of muts) {
      if (m.type === 'attributes') { if (m.target.tagName === 'IMG') watch(m.target); }
      else m.addedNodes.forEach((n) => n.nodeType === 1 && scan(n));
    }
  }).observe(document.documentElement, { childList: true, subtree: true, attributes: true, attributeFilter: ['src'] });
})();
